import type { HydratedAsset } from "@zapier/client";
import {
  AssetTypes,
  normalizeFolderIds,
  normalizeZapNodeId,
} from "@zapier/utils";

/**
 * Typed asset id buckets matching the zsl-api `copy-to-account` request
 * schema. Mirrors the flat-asset input mode (as opposed to `folderId`).
 */
export interface TypedAssetIds {
  zapIds: string[];
  interfaceIds: string[];
  tableIds: string[];
  chatbotIds: string[];
  agentIds: string[];
  canvasIds: string[];
}

export interface AssetSummaryItem {
  type: string;
  count: number;
}

export interface CreatedCopyItem {
  sourceId: string;
  targetId: string;
  type: string;
}

interface CopyToAnotherAccountPayloadInput {
  targetAccountId: string;
  destinationFolderId: string | null;
  folderIds?: string[];
  typedIds: TypedAssetIds;
  selectedItemsAreAllFolders: boolean;
  /**
   * When set, forwarded to zsl-api so it also copies each table's row data
   * (background job). Left unset when no tables are being copied.
   */
  includeTableRecords?: boolean;
}

export interface CopyToAnotherAccountPayload {
  targetAccountId: string;
  destinationFolderId: string | null;
  folderIds?: string[];
  assetIds?: TypedAssetIds;
  includeTableRecords?: boolean;
}

interface ResolveCopyRedirectPathInput {
  destinationFolderId: string | null;
}

interface TopLevelFolderOptionInput {
  hasFolderSource: boolean;
  selectedItemsAreAllFolders: boolean;
}

interface BuildCombinedPreviewAssetsInput {
  assets: HydratedAsset[];
  subtreeAssets: HydratedAsset[];
  subtreeAssetsMulti: HydratedAsset[];
  folderIds?: string[];
  selectedItemsAreAllFolders?: boolean;
}

interface BuildCombinedPreviewAssetsOutput {
  hasFolderSource: boolean;
  areSelectedItemsAllFolders: boolean;
  combinedPreviewAssets: HydratedAsset[];
}

interface IsDestinationDepthAllowedInput {
  hasFolderSource: boolean;
  sourceSubtreeMaxDepth?: number;
  destinationDepth: number;
  maxSubfolderDepth?: number;
}

interface NewFolderDepthInput {
  selectedParentDepth?: number;
}

interface CreateFolderDepthDisabledReasonInput {
  selectedParentDepth?: number;
  hasFolderSource: boolean;
  sourceSubtreeMaxDepth?: number;
  maxSubfolderDepth?: number;
}

export type CreateFolderDepthDisabledReason =
  | "max-subfolder-depth"
  | "copy-projection-depth";

// Keep in sync with org service `MAX_SUBFOLDER_DEPTH`:
// https://gitlab.com/zapier/zapier/-/blob/main/web/backend/zapier/org_service/constants.py#L2
export const MAX_SUBFOLDER_DEPTH = 5;

function getZapIdForZsl(assetId: string): string {
  return assetId.startsWith("zap:") ? assetId.slice(4) : assetId;
}

function getDraftZapId(asset: HydratedAsset): string | undefined {
  // `HydratedAsset` from @zapier/client does not currently expose `draft`,
  // but hydrated Zap rows include `draft.zapId` at runtime.
  const maybeDraft = (asset as { draft?: unknown }).draft;
  if (!maybeDraft || typeof maybeDraft !== "object") return;
  const maybeZapId = (maybeDraft as { zapId?: unknown }).zapId;
  return typeof maybeZapId === "string" ? maybeZapId : undefined;
}

function getCanonicalAssetKey(asset: HydratedAsset): string {
  if (asset.type !== AssetTypes.Zap) {
    return `${asset.type}:${asset.id}`;
  }
  const draftZapId = getDraftZapId(asset);
  return `zap:${normalizeZapNodeId(draftZapId ?? asset.id)}`;
}

export function buildCombinedPreviewAssets(
  input: BuildCombinedPreviewAssetsInput,
): BuildCombinedPreviewAssetsOutput {
  const normalizedFolderIds = normalizeFolderIds(input.folderIds);
  const hasFolderSource = normalizedFolderIds.length > 0;
  const areSelectedItemsAllFolders =
    input.selectedItemsAreAllFolders ?? hasFolderSource;

  const subtreePreviewAssets = hasFolderSource
    ? [...input.subtreeAssets, ...input.subtreeAssetsMulti]
    : input.assets;

  const previewCandidates =
    hasFolderSource && areSelectedItemsAllFolders
      ? subtreePreviewAssets
      : hasFolderSource
        ? [...subtreePreviewAssets, ...input.assets]
        : input.assets;

  const previewAssetsByCanonicalKey = new Map<string, HydratedAsset>();
  for (const asset of previewCandidates) {
    previewAssetsByCanonicalKey.set(getCanonicalAssetKey(asset), asset);
  }

  return {
    hasFolderSource,
    areSelectedItemsAllFolders,
    combinedPreviewAssets: [...previewAssetsByCanonicalKey.values()],
  };
}

/**
 * Whether the copy includes at least one table. Drives the "Copy table
 * records?" choice + Copy-button gating in the review step. Operates over the
 * full preview list so it also catches tables nested inside copied folders.
 */
export function hasTableAssets(assets: HydratedAsset[]): boolean {
  return assets.some((asset) => asset.type === AssetTypes.Table);
}

/** Partition a flat array of hydrated assets into the typed id buckets. */
export function buildTypedIds(assets: HydratedAsset[]): TypedAssetIds {
  const typed: TypedAssetIds = {
    zapIds: [],
    interfaceIds: [],
    tableIds: [],
    chatbotIds: [],
    agentIds: [],
    canvasIds: [],
  };

  for (const asset of assets) {
    switch (asset.type) {
      case AssetTypes.Zap: {
        typed.zapIds.push(getZapIdForZsl(asset.id));
        break;
      }
      case AssetTypes.Table: {
        typed.tableIds.push(asset.id);
        break;
      }
      case AssetTypes.Interface: {
        typed.interfaceIds.push(asset.id);
        break;
      }
      case AssetTypes.Chatbot: {
        typed.chatbotIds.push(asset.id);
        break;
      }
      case AssetTypes.Agent: {
        typed.agentIds.push(asset.id);
        break;
      }
      case AssetTypes.Canvas: {
        typed.canvasIds.push(asset.id);
        break;
      }
      default: {
        break;
      }
    }
  }

  return typed;
}

function hasAnyTypedIds(typed: TypedAssetIds): boolean {
  return (
    typed.zapIds.length > 0 ||
    typed.interfaceIds.length > 0 ||
    typed.tableIds.length > 0 ||
    typed.chatbotIds.length > 0 ||
    typed.agentIds.length > 0 ||
    typed.canvasIds.length > 0
  );
}

export function buildCopyToAnotherAccountPayload(
  input: CopyToAnotherAccountPayloadInput,
): CopyToAnotherAccountPayload {
  const normalizedFolderIds = normalizeFolderIds(input.folderIds);
  const isFolderOnlySelection =
    input.selectedItemsAreAllFolders && normalizedFolderIds.length > 0;

  const payload: CopyToAnotherAccountPayload = {
    targetAccountId: input.targetAccountId,
    destinationFolderId: input.destinationFolderId,
    ...(normalizedFolderIds.length > 0
      ? { folderIds: normalizedFolderIds }
      : {}),
    ...(input.includeTableRecords === undefined
      ? {}
      : { includeTableRecords: input.includeTableRecords }),
  };

  if (isFolderOnlySelection || !hasAnyTypedIds(input.typedIds)) {
    return payload;
  }

  return {
    ...payload,
    assetIds: {
      zapIds: [...input.typedIds.zapIds],
      interfaceIds: [...input.typedIds.interfaceIds],
      tableIds: [...input.typedIds.tableIds],
      chatbotIds: [...input.typedIds.chatbotIds],
      agentIds: [...input.typedIds.agentIds],
      canvasIds: [...input.typedIds.canvasIds],
    },
  };
}

/** Human-readable summary of {@link TypedAssetIds} for modal UI. */
export function buildAssetSummary(typed: TypedAssetIds): AssetSummaryItem[] {
  const entries: AssetSummaryItem[] = [];
  if (typed.zapIds.length > 0) {
    entries.push({ type: "Zap workflows", count: typed.zapIds.length });
  }
  if (typed.interfaceIds.length > 0) {
    entries.push({ type: "Interfaces", count: typed.interfaceIds.length });
  }
  if (typed.tableIds.length > 0) {
    entries.push({ type: "Tables", count: typed.tableIds.length });
  }
  if (typed.chatbotIds.length > 0) {
    entries.push({ type: "Chatbots", count: typed.chatbotIds.length });
  }
  if (typed.agentIds.length > 0) {
    entries.push({ type: "Agents", count: typed.agentIds.length });
  }
  if (typed.canvasIds.length > 0) {
    entries.push({ type: "Canvases", count: typed.canvasIds.length });
  }
  return entries;
}

/**
 * Decide where post-copy redirect should land.
 * Returns a path under `/app/assets/folders`.
 */
export function resolveCopyRedirectPath(
  input: ResolveCopyRedirectPathInput,
): string {
  const { destinationFolderId } = input;
  return destinationFolderId
    ? `/app/assets/folders/${encodeURIComponent(destinationFolderId)}`
    : "/app/assets/folders";
}

export function shouldShowTopLevelFolderOption(
  input: TopLevelFolderOptionInput,
): boolean {
  return input.hasFolderSource && input.selectedItemsAreAllFolders;
}

export function isDestinationDepthAllowed(
  input: IsDestinationDepthAllowedInput,
): boolean {
  if (!input.hasFolderSource) return true;
  if (input.sourceSubtreeMaxDepth === undefined) return true;
  const projectedFinalDepth =
    input.destinationDepth + input.sourceSubtreeMaxDepth;
  return (
    projectedFinalDepth <= (input.maxSubfolderDepth ?? MAX_SUBFOLDER_DEPTH)
  );
}

/**
 * Destination depth (1-indexed) for a newly-created folder:
 * - root-level create (no selected parent) => depth 1
 * - create under selected parent at depth N => depth N + 1
 */
export function getNewFolderDestinationDepth(
  input: NewFolderDepthInput,
): number {
  return (input.selectedParentDepth ?? 0) + 1;
}

export function getCreateFolderDepthDisabledReason(
  input: CreateFolderDepthDisabledReasonInput,
): CreateFolderDepthDisabledReason | undefined {
  const newDestinationDepth = getNewFolderDestinationDepth({
    selectedParentDepth: input.selectedParentDepth,
  });
  const maxDepth = input.maxSubfolderDepth ?? MAX_SUBFOLDER_DEPTH;

  if (newDestinationDepth > maxDepth) {
    return "max-subfolder-depth";
  }

  if (!input.hasFolderSource || input.sourceSubtreeMaxDepth === undefined) {
    return;
  }

  const projectedFinalDepth = newDestinationDepth + input.sourceSubtreeMaxDepth;
  if (projectedFinalDepth > maxDepth) {
    return "copy-projection-depth";
  }
}
